// Updated: All styles inline + toggle show/hide account number per card

import React, { useEffect, useState } from "react";
import { CustomDatePicker } from "components/molecules/CustomDatePicker";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Drawer,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as PaymentSuccessIcon } from "svgs/light/crowSource/paymentSuccessIcon.svg";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useDispatch } from "react-redux";
import { formatIndianCurrency } from "helpers/formateCurrency";
import { raise_toast_message } from "actions/auth.action";
import crowdSourceService from "services/crowdService";
import employeeService from "services/jobmanagementService";

// Day.js setup
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

interface UserModalProps {
  open: boolean;
  onClose: () => void;
  selectedData: any;
  getDates?: any;
  start_date?: string;
  end_date?: string;
}

function formatDate(dateStr: string): string {
  const localTime = dayjs.utc(dateStr).local();
  return localTime.format("ddd, Do MMMM . h:mm a");
}

function getBankNameFromIFSC(ifsc: string): string {
  return ifsc.slice(0, 4).toUpperCase();
}

function maskAccountNumber(accountNumber: string): string {
  const acc = accountNumber.replace(/\s+/g, "");
  if (acc.length <= 8) return acc.replace(/.(?=.{2})/g, "*");
  const start = acc.slice(0, 2);
  const end = acc.slice(-2);
  const masked = "*".repeat(acc.length - start.length - end.length);
  return `${start}${masked}${end}`;
}

const RegisteredUsersModal: React.FC<UserModalProps> = ({
  onClose,
  open,
  selectedData,
    start_date,
    end_date,
}) => {
  const dispatch = useDispatch();
  const [transactions, setTransactions] = useState<any[]>([]);

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    dayjs().subtract(30, "day")
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs());

  // Job list dropdown state
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);

  const getDates = () => {
    const start_date = dayjs(startDate).startOf("day").utc().format();
    const end_date = dayjs(endDate).endOf("day").utc().format();

    return {
      start_date,
      end_date,
    };
  };

  useEffect(() => {
    if (start_date) setStartDate(dayjs(start_date));
  }, [start_date]);

  useEffect(() => {
    if (end_date) setEndDate(dayjs(end_date));
  }, [end_date]);

  useEffect(() => {
    const fetchCurationList = async () => {
      const { start_date, end_date } = getDates();
      const reponse = await crowdSourceService?.fetchCurationList(
        selectedData?.employee_id || "",
        start_date || "",
        end_date || ""
      );
      if (!reponse?.error) setTransactions(reponse?.data || []);
    };

    if (selectedData) {
      fetchCurationList();
    }
  }, [startDate, endDate]);

  // Fetch jobs list when modal opens; keep selection local to avoid side effects
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res: any = await crowdSourceService.fetchEmployeeJobListIDS(
          selectedData?.employee_id || ""
        );

        if (!res.error) {
          const updatedList = res?.data?.map((each: any) => {
            return {
              label: each?.job_title || "",
              value: each?.job_id || "",
            };
          });
          setJobs(updatedList || []);
          if(updatedList?.length > 0){
          setSelectedJob(updatedList[0] || null);
          }
        }
      } catch (e) {
        // silently ignore to avoid disturbing other functionality
      }
    };
    if (open) fetchJobs();
  }, [open]);

  const filterList = () => {
    let filtList = transactions;

    console.log(filtList, "filtList", selectedJob?.value);

    if (selectedJob?.value) {
      filtList =
        filtList.filter((each: any) => {
          console.log(
            each?.job_id,
            selectedJob?.value,
            each?.job_id === selectedJob?.value
          );

          return each?.job_id === selectedJob?.value;
        }) || [];
    }
    return filtList;
  };

  return (
    <Drawer
      anchor="right"
      sx={{
        width: "470px",
        "& .MuiDrawer-paper": {
          width: "470px",
          padding: "20px",
          boxSizing: "border-box",
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Box>
        <Box
          style={{
            width: "100%",
            maxWidth: "470px",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Curation List
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider style={{ marginTop: "10px", marginBottom: "20px" }} />

          {/* Date Filter Inputs */}
          <Box
            style={{
              marginBottom: "20px",
            }}
          >
            <Box style={{ marginBottom: "10px" }}>
              <Typography variant="body2">Job ID</Typography>
              <Autocomplete
                size="small"
                options={jobs}
                getOptionLabel={(option: any) => option?.label}
                value={selectedJob}
                onChange={(_, val) => setSelectedJob(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select job ID"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontFamily: "Lexend, Inter, sans-serif",
                        fontSize: 14,
                        height: 36,
                        paddingLeft: 8,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E6E3E3",
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box>
                <Typography variant="body2">Start Date</Typography>
                <CustomDatePicker
                  label="Start Date"
                  format="DD/MM/YYYY"
                  value={startDate}
                  onChange={(date: dayjs.Dayjs | null) => setStartDate(date)}
                />
              </Box>
              <Box>
                <Typography variant="body2">End Date</Typography>
                <CustomDatePicker
                  label="End Date"
                  format="DD/MM/YYYY"
                  value={endDate}
                  minDate={startDate ? startDate.add(0, "day") : undefined} // must be > start date
                  onChange={(date: dayjs.Dayjs | null) => setEndDate(date)}
                />
              </Box>
            </Box>
          </Box>

          <Box style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}>
            {filterList().length > 0 && (
              <>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Total Earnings: Rs.{" "}
                  {formatIndianCurrency(
                    filterList().reduce(
                      (sum, txn) => sum + txn.amount_earned,
                      0
                    )
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: "#666",
                    marginBottom: 20,
                  }}
                >
                  {filterList().length} QA Sessions
                </Typography>

                <Paper
                  elevation={0}
                  style={{
                    background: "#F7F7F7",
                    borderRadius: 10,
                    padding: 16,
                    marginBottom: 16,
                    border: "1px solid #E6E3E3",
                  }}
                >
                  <Typography style={{ fontSize: 13, color: "#666" }}>
                    Performance Summary
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginTop: 8 }}
                  >
                    <Typography style={{ fontSize: 14 }}>
                      Avg Accuracy:{" "}
                      {filterList().length > 0
                        ? (
                            filterList().reduce(
                              (sum: any, txn: any) => sum + txn.accuracy,
                              0
                            ) / filterList().length
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                      Avg Speed:{" "}
                      {filterList().length > 0
                        ? (
                            filterList().reduce(
                              (sum, txn) => sum + txn.speed_sec,
                              0
                            ) / filterList().length
                          ).toFixed(1)
                        : 0}
                      s
                    </Typography>
                  </Box>
                </Paper>
              </>
            )}

            {filterList().map((txn, index) => (
              <Paper
                key={index}
                elevation={0}
                style={{
                  background: "#F7F7F7",
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 16,
                  border: "1px solid #E6E3E3",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ marginBottom: 8 }}
                >
                  <Typography style={{ fontSize: 13, color: "#666" }}>
                    {formatDate(txn.date)}
                  </Typography>
                  <Typography
                    style={{ fontWeight: 500, fontSize: 15, color: "#7553FF" }}
                  >
                    Rs. {formatIndianCurrency(txn.amount_earned)}
                  </Typography>
                </Box>

                <Typography
                  style={{ fontWeight: 500, fontSize: 15, marginBottom: 8 }}
                >
                  Job ID: {txn.job_id}
                </Typography>

                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <Typography variant="body2" style={{ color: "#666" }}>
                    QA Transactions: {txn.total_qa_transactions}
                  </Typography>
                  <Typography variant="body2" style={{ color: "#666" }}>
                    QA Count: {txn.qa_count}
                  </Typography>
                </Box>

                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" style={{ color: "#666" }}>
                    Accuracy: {txn.accuracy}%
                  </Typography>
                  <Typography variant="body2" style={{ color: "#666" }}>
                    Speed: {txn.speed_sec}s
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default RegisteredUsersModal;
